import { fetchFromRTM } from './index';
import * as constants from './constants';
import './typedef';

class InteractiveResolver {
  constructor(connection) {
    this.connection = connection;
  }

  /**
   * see [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-interactive-create/)
   * @param {Object} payload
   * @param {string} payload.pathwayId
   * @param {string} payload.pluginId
   * @param {string} payload.pluginVersion
   * @param {string=} payload.config
   * @returns {Promise.<InteractiveDTO>}
   */
  authorInteractiveCreate = async ({ pathwayId, pluginId, pluginVersion, config }) => {
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_CREATE,
      pathwayId,
      pluginId,
      pluginVersion,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_CREATE_OK,
      constants.RTM_AUTHOR_INTERACTIVE_CREATE_ERROR,
    );

    return response.interactive;
  }

  /**
   * Fetches an interactive
   *   see [Rtm Docs](https://dev-developers.smartsparrow.com/messages/author-interactive-get/)
   * @param {string} interactiveId
   * @returns {Promise.<InteractiveDTO>}
   */
  authorInteractiveGet = async (interactiveId) => {
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_GET,
      interactiveId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_GET_OK,
      constants.RTM_AUTHOR_INTERACTIVE_GET_ERROR,
    );
    return response.interactive;
  }

  /**
   * Deletes an interactive child from a pathway
   *   see [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-interactive-delete/)
   * @param {string} interactiveId
   * @param {string} parentPathwayId
   * @returns {Promise} Resolves to the interactiveId and parentPathwayId of the
   *   deleted interactive
   */
  authorInteractiveDelete = async (interactiveId, parentPathwayId) => {
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_DELETE,
      interactiveId,
      parentPathwayId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_DELETE_OK,
      constants.RTM_AUTHOR_INTERACTIVE_DELETE_ERROR,
    );
    return response.interactive;
  };

  /**
   * Replace a configuration for an interactive. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-interactive-config-replace/)
   * @param {string} interactiveId
   * @param {string} config as a jSON string
   * @returns {Promise<string>}
   */
  authorInteractiveConfigReplace = async (interactiveId, config) => {
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_CONFIG_REPLACE,
      interactiveId,
      config,
    };


    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_CONFIG_REPLACE_OK,
      constants.RTM_AUTHOR_INTERACTIVE_CONFIG_REPLACE_ERROR,
    );
    return response.config;
  };

  /**
   * Copies an interactive into a pathway. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-interactive-duplicate/)
   * @param {Object} payload
   * @param {string} payload.interactiveId
   * @param {string} payload.pathwayId
   * @param {number} payload.index
   * @returns {InteractiveDTO}
   */
  authorInteractiveDuplicate = async ({ interactiveId, pathwayId, index, customErrorMessage }) => {
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_DUPLICATE,
      interactiveId,
      pathwayId,
      index,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_DUPLICATE_OK,
      constants.RTM_AUTHOR_INTERACTIVE_DUPLICATE_ERROR,
      customErrorMessage,
    );
    return response.interactive;
  };

  /**
   * Move components of interactive and activity. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-components-move)
   * @param {[string]} componentIds
   * @param {string} interactiveId
   * @param {string} elementType
   * @returns {Promise.<void>} An empty promise
   */
  authorInteractiveMove = async ({ componentIds, interactiveId, elementType }) => {
    const message = {
      type: constants.RTM_AUTHOR_COMPONENTS_MOVE,
      componentIds,
      elementId: interactiveId,
      elementType,
    };

    await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_COMPONENTS_MOVE_OK,
      constants.RTM_AUTHOR_COMPONENTS_ERROR,
    );
  };

  /**
   * Register an element with a plugin reference to a student scope. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-student-scope-register/)
   * @param {Object} payload
   * @param {string} payload.studentScopeURN
   * @param {string} payload.elementId
   * @param {string} payload.elementType
   * @returns {Promise.<void>} An empty promise
   */
  authorStudentScopeRegister = async ({ studentScopeURN, elementId, elementType }) => {
    const message = {
      type: constants.RTM_AUTHOR_STUDENT_SCOPE_REGISTER,
      elementId,
      elementType,
      studentScopeURN,
    };

    await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_STUDENT_SCOPE_REGISTER_OK,
      constants.RTM_AUTHOR_STUDENT_SCOPE_REGISTER_ERROR,
    );
  };

  /**
   * Deregister an element from a student scope. see
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-student-scope-deregister/)
   * @param {Object} payload
   * @param {string} payload.studentScopeURN
   * @param {string} payload.elementId
   * @param {string} payload.elementType
   * @returns {Promise.<void>} An empty promise
   */
  authorStudentScopeDeregister = async ({ studentScopeURN, elementId, elementType }) => {
    const message = {
      type: constants.RTM_AUTHOR_STUDENT_SCOPE_DEREGISTER,
      elementId,
      elementType,
      studentScopeURN,
    };

    await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_STUDENT_SCOPE_DEREGISTER_OK,
      constants.RTM_AUTHOR_STUDENT_SCOPE_DEREGISTER_ERROR,
    );
  }

  /**
   * Move an interactive to another pathway. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-interactive-move/)
   * @param {[string]} interactiveId
   * @param {string} pathwayId
   * @param {string} index
   * @returns {Promise.<void>} An empty promise
   */
  authorInteractiveMoveToPathway = async ({ interactiveId, pathwayId, index = '', isIndexAllowed }) => {
    let modifiedIndex = index;
    if (modifiedIndex === 0 && !isIndexAllowed) {
      modifiedIndex = undefined;
    }
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_MOVE,
      interactiveId,
      pathwayId,
      index: modifiedIndex,
    };

    await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_MOVE_OK,
      constants.RTM_AUTHOR_INTERACTIVE_MOVE_ERROR,
    );
  };

  /**
   * Add conditional to an element for MD. See
   * @param {Object} payload
   * @param {string} payload.courseId - The course ID (timeuuid)
   * @param {string} payload.elementId - The element ID (timeuuid)
   * @param {string} payload.elementType - ACTIVITY, INTERACTIVE, or COMPONENT
   * @param {string=} payload.referenceId - Optional ref ID for items within the element
   * @param {string} payload.conditionalType - OUTPUT_TYPE or MARKET_DERIVATIVE
   * @returns {Promise.<void>} An empty promise
   */
  coursewareElementConditionalAdd = async ({ courseId, elementId, elementType, referenceId, conditionalType }) => {
    const message = {
      type: constants.RTM_COURSEWARE_ELEMENT_CONDITIONAL_ADD,
      courseId,
      elementId,
      elementType,
      conditionalType,
    };

    // Only add referenceId if it's provided
    if (referenceId) {
      message.referenceId = referenceId;
    }

    await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_COURSEWARE_ELEMENT_CONDITIONAL_ADD_OK,
      constants.RTM_COURSEWARE_ELEMENT_CONDITIONAL_ADD_ERROR,
    );
  };

  /**
   * Remove conditional from an element for MD. See
   * @param {Object} payload
   * @param {string} payload.courseId - The course ID (timeuuid)
   * @param {string} payload.elementId - The element ID (timeuuid)
   * @param {string} payload.elementType - ACTIVITY, INTERACTIVE, or COMPONENT
   * @param {string=} payload.referenceId - Optional ref ID for items within the element
   * @param {string} payload.conditionalType - OUTPUT_TYPE or MARKET_DERIVATIVE
   * @returns {Promise.<void>} An empty promise
   */
  coursewareElementConditionalRemove = async ({ courseId, elementId, elementType, referenceId, conditionalType }) => {
    const message = {
      type: constants.RTM_COURSEWARE_ELEMENT_CONDITIONAL_REMOVE,
      courseId,
      elementId,
      elementType,
      conditionalType,
    };

    // Only add referenceId if it's provided
    if (referenceId) {
      message.referenceId = referenceId;
    }

    await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_COURSEWARE_ELEMENT_CONDITIONAL_REMOVE_OK,
      constants.RTM_COURSEWARE_ELEMENT_CONDITIONAL_REMOVE_ERROR,
    );
  };
}

export default InteractiveResolver;
